import { addMonthsToDate, formatFullDate } from "@/lib/format";
import { editionLabel } from "@/lib/license";
import type { AdminLicense } from "@/pages/admin/hooks/useAdminData";

interface BatchRenewConfirmModalProps {
  open: boolean;
  licenses: AdminLicense[];
  months: number;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function BatchRenewConfirmModal({
  open,
  licenses,
  months,
  busy,
  onConfirm,
  onCancel,
}: BatchRenewConfirmModalProps) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center px-4 py-8">
      <button
        type="button"
        aria-label="关闭确认弹窗"
        onClick={() => {
          if (!busy) onCancel();
        }}
        className="absolute inset-0 cursor-pointer bg-foreground-950/50"
      />

      <div
        role="dialog"
        aria-modal="true"
        className="lh-fade-up relative z-10 flex max-h-full w-full max-w-xl flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50"
      >
        <div className="flex items-start gap-3 border-b border-background-200 px-5 py-4">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
            <i className="ri-calendar-check-line text-xl" />
          </span>
          <div className="flex-1">
            <h2 className="font-heading text-base font-semibold text-foreground-950">
              确认批量续期 {licenses.length} 张许可证？
            </h2>
            <p className="mt-1 text-xs leading-relaxed text-foreground-600">
              以下许可证将在当前到期时间基础上顺延 <strong className="text-foreground-950">{months} 个月</strong>
              （若已过期则从今天起算）。执行后不可撤销，请再次核对新到期日。
            </p>
          </div>
          <button
            type="button"
            disabled={busy}
            onClick={onCancel}
            aria-label="关闭"
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <i className="ri-close-line" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <ul className="space-y-2.5">
            {licenses.map((license, index) => {
              const currentExpiry = license.expires_at ? formatFullDate(license.expires_at) : "永久 / 未设置";
              const nextExpiry = formatFullDate(addMonthsToDate(license.expires_at, months));
              return (
                <li
                  key={license.id}
                  className="flex items-start gap-3 rounded-md border border-background-200 bg-background-50 px-3.5 py-3"
                >
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-background-100 text-[11px] font-medium text-foreground-600">
                    {index + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-heading text-sm font-semibold text-foreground-950">{license.product_name}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-foreground-500">
                      <span className="rounded-md bg-background-100 px-2 py-0.5 text-foreground-700">
                        {editionLabel(license.edition)}
                      </span>
                      <span className="font-mono">LH-••••-{license.license_key.slice(-4)}</span>
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-[11px] text-foreground-400 line-through">{currentExpiry}</p>
                    <p className="mt-0.5 flex items-center justify-end gap-1 text-xs font-medium text-primary-700">
                      <span className="flex h-3.5 w-3.5 items-center justify-center">
                        <i className="ri-arrow-right-line" />
                      </span>
                      {nextExpiry}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="flex flex-wrap justify-end gap-2 border-t border-background-200 bg-background-50 px-5 py-4">
          <button
            type="button"
            disabled={busy}
            onClick={onCancel}
            className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            返回检查
          </button>
          <button
            type="button"
            disabled={busy || licenses.length === 0}
            onClick={onConfirm}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-calendar-check-line" />
            </span>
            {busy ? "处理中…" : `确认续期（${licenses.length}）`}
          </button>
        </div>
      </div>
    </div>
  );
}